import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import type { GasSupplyZoneCommitment } from '../../lib/api';

interface Props {
  zone: GasSupplyZoneCommitment;
}

const MW_PER_MMCF = 5.07;
const BCFD_TO_GW = MW_PER_MMCF; // 1 Bcf/d = 1000 MMcf/d × 5.07 MW/MMcf = 5,070 MW = 5.07 GW

const formatGw = (bcfd: number | null) => {
  if (bcfd == null) return null;
  const gw = bcfd * BCFD_TO_GW;
  return gw >= 1 ? `${gw.toFixed(1)} GW` : `${Math.round(gw * 1000)} MW`;
};

const formatAcres = (n: number | null) => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ac`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k ac`;
  return `${n.toLocaleString()} ac`;
};

export const OperatorGasZoneCallout: React.FC<Props> = ({ zone }) => {
  const gwLabel = formatGw(zone.peakBcfd);
  const acLabel = formatAcres(zone.dedicatedAcres);
  const inventoryLabel = zone.inventoryYears ? `${zone.inventoryYears}-yr inventory` : null;

  // Server returns the recipient-appropriate URL shape: a hash route
  // (`/#/zones/<slug>`) for authed admins so the in-app overlay handles it,
  // or a path route with `?token=` for share-link recipients so the standalone
  // ZoneSharePage handles it. Use as-is.
  const zoneHref = zone.zoneUrl;

  return (
    <div
      style={{
        marginBottom: '14px',
        padding: '10px 14px',
        border: `1px solid ${REPORT.accent}40`,
        background: `${REPORT.accent}08`,
        borderRadius: '4px',
        fontSize: '11px',
        lineHeight: 1.5,
        color: REPORT.body,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '9px',
            fontWeight: 700,
            color: REPORT.accent,
            letterSpacing: '0.06em',
            marginBottom: '3px',
          }}
        >
          INSIDE THE {zone.operatorName.toUpperCase()} SUPPLY ZONE
        </div>
        <div style={{ color: REPORT.body }}>
          {zone.assetName}
          {gwLabel ? <> · <strong>{gwLabel}</strong> peak supply</> : null}
          {acLabel ? <> · {acLabel} of operator-controlled acreage</> : null}
          {inventoryLabel ? <> · {inventoryLabel}</> : null}
          .
          <span style={{ color: REPORT.tertiary }}>
            {' '}Pipeline access is non-binding inside the zone; gas comes from the operator's gathering system.
          </span>
        </div>
      </div>
      <a
        href={zoneHref}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: REPORT.accent,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          padding: '4px 10px',
          border: `1px solid ${REPORT.accent}`,
          borderRadius: '3px',
        }}
      >
        Zone deck →
      </a>
    </div>
  );
};
